import { useContext, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { styled } from "@mui/material/styles";
import ListItem from "@mui/material/ListItem";
import Tooltip from "@mui/material/Tooltip";
import MuiTextField from "@mui/material/TextField";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import { THEME_COLORS, THEME_TYPES } from "constants/theme";
import { ComponentPropsContext, ComponentPropsContextApi } from "layouts/Flow Designer/context";
import { useAuth0 } from "@auth0/auth0-react";
import EditorInput from "../EditorInput";
import { getCookie, combineStyles } from "helpers/common";
import { getEnv } from "helpers/environment";

const TextField = styled(MuiTextField)(({ theme }) => ({
  "& .MuiFormLabel-root": {
    fontSize: "12px",
    paddingLeft: "12px",
    lineHeight: "32px",

    "&.MuiInputLabel-shrink": {
      lineHeight: "20px",
      transform: "translate(0, -3px) scale(1)"
    },

    "&.Mui-disabled": {
      opacity: "0.3"
    }
  },
  "& .MuiInputBase-root": {
    "&.Mui-disabled": {
      backgroundColor: `${THEME_COLORS[theme.palette.mode.toUpperCase()].DISABLED_BG} !important`,
      opacity: theme.palette.mode === THEME_TYPES.DARK ? 0.5 : 1
    }
  }
}));

const styles = {
  w100: {
    width: "100%"
  },
  listItemMargin_t16: {
    marginTop: "16px"
  }
};

const ReadOnlyListItem = ({ action, fieldValue }) => {
  const { user } = useAuth0();
  const { actionForm } = useContext(ComponentPropsContext);
  const { setActionForm } = useContext(ComponentPropsContextApi);
  const { currentSavedFlow } = useSelector((store) => store?.designer?.flows);
  const [isCopied, setIsCopied] = useState(false);
  const [isCopiedToken, setIsCopiedToken] = useState(false);
  const token = getCookie("accessToken");
  const graphqlBodyData = JSON.stringify(
    {
      tenant: user?.stripeId,
      input: {
        flow_id: currentSavedFlow?.id,
        payload: {}
      }
    },
    null,
    2
  );

  let defaultValue = "";
  switch (action.field) {
    case "tenantId":
      defaultValue = user?.stripeId;
      break;
    case "flowId":
      defaultValue = currentSavedFlow?.id;
      break;
    case "executorEndpoint":
      defaultValue = getEnv("EXECUTOR_API");
      break;
    case "endpoint":
      defaultValue = getEnv("EXECUTOR_API_SOCKET") + "/restendpoint";
      break;
    case "restEndpoint":
      defaultValue =
        getEnv("EXECUTOR_API_SOCKET") +
        `/webhook?tenant_id=${user?.stripeId}&flow_id=${currentSavedFlow?.id}`;
      break;
    case "graphqlBody":
      defaultValue = graphqlBodyData;
      break;
    default:
      defaultValue = fieldValue;
  }

  useEffect(() => {
    if (!actionForm[action.field]?.value) {
      setActionForm((prevForm) => ({
        ...prevForm,
        [action.field]: { ...prevForm[action.field], value: defaultValue }
      }));
    }
  }, []);

  const copyToClipboard = (text, field) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        if (field === "accessToken") {
          setIsCopiedToken(true);
          // Reset the copied status after a delay (e.g., 2 seconds)
          setTimeout(() => {
            setIsCopiedToken(false);
          }, 2000);
        } else {
          setIsCopied(true);
          // Reset the copied status after a delay (e.g., 2 seconds)
          setTimeout(() => {
            setIsCopied(false);
          }, 2000);
        }
      })
      .catch((error) => {
        console.error("Error copying text to clipboard:", error);
      });
    // Add any notification or state change here to indicate the text has been copied
  };

  const handleInstructions = () => {
    if (action.field == "restEndpoint") {
      return (
        <>
          <ListItem
            key={action.field + "-1"}
            sx={combineStyles(styles.w100, styles.listItemMargin_t16)}
          >
            <SoftTypography sx={{ fontSize: 12, marginLeft: "12px", width: "100%" }}>
              {"Add an Authorization on your Header and use your accessToken"}
            </SoftTypography>
            <Tooltip title={isCopiedToken ? "Copied" : "Copy"}>
              <SoftButton
                color="primary"
                variant="text"
                onClick={() => copyToClipboard(token, "accessToken")}
              >
                get token {isCopiedToken ? <CheckCircleIcon /> : <ContentCopyIcon />}
              </SoftButton>
            </Tooltip>
          </ListItem>
          <ListItem
            key={action.field + "+2"}
            sx={{
              ...combineStyles(styles.w100, styles.listItemMargin_t16),
              marginTop: "0 !important"
            }}
          >
            <SoftTypography sx={{ fontSize: 12, marginLeft: "12px", width: "100%" }}>
              {"You can add more query string parameters for your payload"}
            </SoftTypography>
          </ListItem>
        </>
      );
    } else if (action.field === "graphqlBody") {
      return (
        <ListItem key={action.field} sx={combineStyles(styles.w100, styles.listItemMargin_t16)}>
          <SoftTypography sx={{ fontSize: 12, marginLeft: "12px", width: "100%" }}>
            {"Add an Authorization on your Header and use your accessToken"}
          </SoftTypography>
          <Tooltip title={isCopiedToken ? "Copied" : "Copy"}>
            <SoftButton
              color="primary"
              variant="text"
              onClick={() => copyToClipboard(token, "accessToken")}
            >
              get token {isCopiedToken ? <CheckCircleIcon /> : <ContentCopyIcon />}
            </SoftButton>
          </Tooltip>
        </ListItem>
      );
    }
  };

  return (
    <>
      {action.field === "graphqlBody" && (
        <div
          style={{
            display: "flex",
            justifyContent: "start",
            alignItems: "center",
            margin: "20px 0 -10px 10px"
          }}
        >
          <SoftTypography sx={{ fontSize: 12 }}>{"Body"}</SoftTypography>
          <Tooltip title={isCopied ? "Copied" : "Copy"}>
            <SoftButton
              color="primary"
              variant="text"
              onClick={() => copyToClipboard(graphqlBodyData, action.label)}
            >
              {isCopied ? <CheckCircleIcon /> : <ContentCopyIcon />}
            </SoftButton>
          </Tooltip>
        </div>
      )}
      <ListItem key={action.label} sx={combineStyles(styles.w100, styles.listItemMargin_t16)}>
        {action.field !== "graphqlBody" ? (
          <SoftBox display="flex" alignItems="end" justifyContent="space-between" width="100%">
            <TextField
              sx={styles.w100}
              label={action.label}
              slotProps={{
                input: {
                  disabled: true
                }
              }}
              defaultValue={defaultValue}
              type="text"
              variant="standard"
            />
            <Tooltip title={isCopied ? "Copied" : "Copy"}>
              <SoftButton
                color="primary"
                variant="text"
                onClick={() => copyToClipboard(defaultValue, action.label)}
                sx={{ marginBottom: "5px" }}
              >
                {isCopied ? <CheckCircleIcon /> : <ContentCopyIcon />}
              </SoftButton>
            </Tooltip>
          </SoftBox>
        ) : (
          <EditorInput
            fieldValue={defaultValue}
            language="json"
            options={{
              wordWrap: "off",
              scrollBeyondLastLine: false,
              scrollbar: {
                vertical: "hidden",
                horizontal: "hidden"
              },
              readOnly: true
            }}
            height={"150px"}
          />
        )}
      </ListItem>
      {handleInstructions()}
    </>
  );
};

export default ReadOnlyListItem;
